/**
 * Recipes / par product-gate: Add puts the catalogue row on the shopping
 * list in one click. No zone / qty / par / favourite / basket modal.
 * Reuses POST venue/:id/product — do not add a Server route.
 */

import { isGrowthEngineCatalogueProductGate } from './growthEngineOneJobPage'

export function shouldUseCatalogueProductGateQuickAdd(pathname, search) {
  return isGrowthEngineCatalogueProductGate(pathname, search)
}

function catalogueSupplierTitle(product) {
  const supplier = product?.supplier
  if (supplier && typeof supplier === 'object') {
    return String(supplier.title || product?.supplierTitle || '').trim()
  }
  return String(product?.supplierTitle || '').trim()
}

export function getCatalogueProductSupplierTitle(product) {
  return catalogueSupplierTitle(product)
}

function catalogueSupplierId(product) {
  const supplier = product?.supplier
  if (supplier && typeof supplier === 'object') {
    return supplier._id || supplier
  }
  return supplier
}

function toPositiveNumber(value, fallback) {
  if (value == null || value === '') return fallback
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

function toPositiveNumberOrNull(value) {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * POST venue/:id/product body. Always send ids (not catalogue/supplier
 * documents) and never empty type/zone — Joi rejects "" and mongoose
 * cannot cast a full product object as ObjectId.
 */
export function buildCatalogueAddPayload(product, venueId, extras = {}) {
  const supplierSource = {
    supplier: extras.supplier || product?.supplier,
    supplierTitle: extras.supplierTitle || product?.supplierTitle,
  }
  const supplierTitle =
    String(extras.supplierTitle || '').trim() ||
    catalogueSupplierTitle(supplierSource) ||
    catalogueSupplierTitle(product)
  const type =
    String(extras.type ?? product?.type ?? '').trim() || 'Default Type'
  const zone =
    String(extras.zone || supplierTitle || '').trim() || 'Default Zone'
  return {
    title: String(extras.title ?? product?.title ?? '').trim() || 'Product',
    type,
    UoM: extras.UoM || product?.UoM || '',
    supplier: catalogueSupplierId(supplierSource),
    supplierTitle,
    zone,
    secondaryZone:
      extras.secondaryZone == null ? '' : String(extras.secondaryZone),
    defaultQuantity: toPositiveNumber(extras.defaultQuantity, 1),
    monthlyLimit: toPositiveNumberOrNull(extras.monthlyLimit),
    isFavourite: Boolean(extras.isFavourite),
    product: product?._id || product,
    venue: venueId,
  }
}

/**
 * Same add API as the catalogue modal confirm, with supplier title as the
 * zone default, qty 1, not a favourite, no monthly limit, no basket.
 */
export function buildCatalogueProductGateAddPayload(product, venueId) {
  return buildCatalogueAddPayload(product, venueId)
}

export function getVenueProductSaveErrorMessage(error) {
  const data = error?.response?.data
  const raw =
    (typeof data === 'string' && data) ||
    data?.message ||
    data?.error ||
    error?.message
  const text = String(raw || '')
    .replace(/^"+|"+$/g, '')
    .trim()
  if (/type.*empty/i.test(text)) {
    return 'Type cannot be blank. Pick or create a type, then try again.'
  }
  if (text && !/^request failed$/i.test(text)) {
    return text
  }
  return 'The product was not added. Please try again.'
}

export const CATALOGUE_LIST_ADD_LABEL = 'Add'
export const CATALOGUE_BASKET_ADD_LABEL = 'Add'
export const CATALOGUE_ADD_ALREADY_ON_LIST_LABEL = 'Already on list'
export const CATALOGUE_ADD_ALREADY_IN_BASKET_LABEL = 'Already in basket'
export const CATALOGUE_ADD_TO_LIST_FIRST_LABEL = 'Add to list first'

/**
 * Catalogue has two Add actions. Disabled ones used to say “Added” or a
 * mute grey Add, which looks broken on first-try. Say why on the control.
 */
export function getCatalogueAddButtonState({
  target,
  onShoppingList,
  inBasket = false,
  adding = false,
  enabledLabel,
} = {}) {
  const addLabel =
    enabledLabel ||
    (target === 'basket'
      ? CATALOGUE_BASKET_ADD_LABEL
      : CATALOGUE_LIST_ADD_LABEL)

  if (target === 'basket') {
    if (inBasket) {
      return {
        disabled: true,
        label: CATALOGUE_ADD_ALREADY_IN_BASKET_LABEL,
        tooltip: 'This product is already in your basket.',
        showQuantity: true,
      }
    }
    if (!onShoppingList) {
      return {
        disabled: true,
        label: CATALOGUE_ADD_TO_LIST_FIRST_LABEL,
        tooltip: 'Add this to your shopping list first.',
        showQuantity: false,
      }
    }
    return {
      disabled: Boolean(adding),
      label: addLabel,
      tooltip: '',
      showQuantity: false,
    }
  }

  if (onShoppingList) {
    return {
      disabled: true,
      label: CATALOGUE_ADD_ALREADY_ON_LIST_LABEL,
      tooltip: 'This product is already on your shopping list.',
      showQuantity: false,
    }
  }

  return {
    disabled: Boolean(adding),
    label: addLabel,
    tooltip: '',
    showQuantity: false,
  }
}
